import { DEFAULT_ALL_VALUE, isCommandKeyPressed, randomString, useHover, useTextWidth } from '@chronosphereio/core';
import {
  outlinedInputClasses,
  // This is a temporary override for an old use case until we complete the Chrono UI Popover backfill (sc-138310)
  Popover,
  // This component uses some props we don't expose in the Chrono UI TextField component
  // so import directly from MUI.
  TextField,
} from '@mui/material';
import { useDebouncedEffect } from '@react-hookz/web';
import { Check as CheckIcon, Plus as PlusIcon, Magnify as Search } from 'mdi-material-ui';
import numbro from 'numbro';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useTheme } from '../../theme/useTheme';
import { CommonDataProps } from '../../utils';
import { Box } from '../Box';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import {
  ClickAwayListener,
  InputAdornment,
  LinearProgress,
  ListItem,
  ListItemProps,
  ListItemText,
  Skeleton,
} from '../proxied-components';
import { Stack } from '../Stack';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';

const INPUT_MIN_WIDTH = 120;
const INPUT_MAX_WIDTH = 350;

type Option = {
  value: string;
  description?: string;
  label?: string;
};

type ComboboxBaseProps = CommonDataProps & {
  /**
   * Whether to allow selecting items that are typed in but not in the options list.
   */
  allowCustomValue?: boolean;
  /**
   * Value that represents the ALL option.  If an option is selected that matches this value, all other options will be deselected
   */
  allValue?: string;
  /**
   * If provided, the border color will be overridden. Can be any valid color from the theme (e.g. "warning.main").
   */
  borderColor?: string;
  /**
   * Whether the combobox is disabled.
   */
  disabled?: boolean;
  /**
   * A function to filter the options based on the input value. If not included, options will be filtered based on string match.
   */
  filterOptions?: (
    options: string[],
    inputValue: string
  ) => {
    matches: string[];
    // matchesHighlightedRanges is an array of pairs of numbers, where the first number is the start index and the second number is the end index.
    matchesHighlightedRanges?: Array<number[] | undefined>;
  };
  /**
   * highlightedRanges is an index (matches the options array) of what ranges of text should be highlighted.
   * It is an array of pairs of numbers, where the first number is the start index and the second number is the end index.
   * Optional, provided by the parent seach engine. If not provided, we default to regex based highlighting.
   */
  highlightedRanges?: number[][];
  /**
   * Whether to highlight the search keywords when displaying the options.
   */
  highlightSearchKeywords?: boolean;
  /**
   * Unique identifier for the combobox. Defaults to 'combobox'.
   */
  id?: string;
  /**
   * Whether the combobox is loading. When true, will show a loading state in the options list
   */
  isLoading?: boolean;
  /**
   * The label that gets applied to the input
   */
  label?: string;
  /**
   * The text to display when there are no options. Will only show if "allowCustomValue" is false.
   */
  noOptionsText?: string;
  /**
   * If provided, a "Reset" button will appear in the header. This callback will be triggered when the "Reset" button is clicked; the Combobox will be reset to the returned value.
   */
  onReset?: () => string | null;

  /**
   * The available options to select from
   */
  options?: Option[] | string[];
  /**
   * The placeholder text to display in the input when there is no value.
   */
  placeholder?: string;
  /**
   * A function used to render the footer.  Default will render the count of options.
   */
  renderFooter?: (props: ComboboxInternalProps) => React.ReactNode;
  /**
   * A function to render the current value. Default will use a ComboboxSelectTrigger. Use this when you want fine-grained control over how the value is displayed
   */
  renderValue?: React.ReactNode;
  /**
   * Debounce time in milliseconds for the search value change. Defaults to 0 (no debounce).
   */
  searchDebounceMs?: number;
  /**
   * The placeholder text to display in the search bar when there is no search value.
   */
  searchPlaceholder?: string;
  /**
   * A function used to render the secondary content. This is the content that is displayed to the right of the options list as you hover over an option
   */
  secondaryContent?: (ctx: { handleClose: () => void; highlightedOptionValue?: string }) => React.ReactNode;
  /**
   * Whether to show the footer.
   */
  showFooter?: boolean;
};

type SingleSelect = {
  /**
   * A function to call when the value is selected.
   */
  onSelect: (value: string | null) => void;
  /**
   * The current value of the combobox.
   */
  value: string | null;
  /**
   * Whether the combobox allows selecting multiple values
   */
  isMulti?: false;
} & ComboboxBaseProps;

type MultiSelect = {
  /**
   * Whether the combobox allows selecting multiple values
   */
  isMulti: true;

  /**
   * A function to call when the value is selected.
   */
  onSelect: (value: string[] | null) => void;
  /**
   * The current selected values.
   */
  value: string[] | null;
} & ComboboxBaseProps;

export type ComboboxProps = SingleSelect | MultiSelect;

// Helper function to get the selected values from the props
function getSelectedValuesFromProps(value: string | string[] | null): string[] {
  if (value === null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
}

export const ComboboxKeyboardEvents = {
  shouldOpenCombobox,
};

/**
 * Whether or not a KeyboardEvent should cause the Combobox to open.
 */
function shouldOpenCombobox(event: React.KeyboardEvent) {
  return (event.key === 'Enter' && !isCommandEnterShortcut(event)) || event.key === ' ' || event.key === 'ArrowDown';
}

const DEFAULT_HIGHLIGHTED_INDEX = -1;

export type ComboboxInternalProps = ReturnType<typeof useCombobox>;

// TODO: Break down into smaller hooks
function useCombobox({
  id = 'combobox',
  options: propOptions = [],
  onSelect,
  value,
  isMulti,
  allowCustomValue = true,
  allValue = DEFAULT_ALL_VALUE,
  filterOptions,
  searchDebounceMs = 0,
}: ComboboxProps) {
  const selectedValues = useMemo(() => {
    return getSelectedValuesFromProps(value);
  }, [value]);

  // Convert the options to a consistent format
  const rawOptions = useMemo(
    () => propOptions.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)),
    [propOptions]
  );

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>(selectedValues);

  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(DEFAULT_HIGHLIGHTED_INDEX);
  // const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mainInputRef = useRef<HTMLDivElement>(null);
  const open = Boolean(anchorEl);

  const [rawInputValue, setRawInputValue] = useState('');
  useDebouncedEffect(
    () => {
      setInputValue(rawInputValue);
    },
    [rawInputValue],
    searchDebounceMs
  );

  const getOptionLabel = (optionKey: string) => {
    const option = rawOptions.find((o) => o.value === optionKey);
    return option?.label ?? option?.value ?? optionKey;
  };

  const getOptionDescription = (optionKey: string) => {
    const option = rawOptions.find((o) => o.value === optionKey);
    return option?.description;
  };

  const options = useMemo(() => {
    const rawOptionKeys = rawOptions.map((o) => o.value);
    // sort the selected values to the top
    const selected = selectedValues.filter((v) => rawOptionKeys.includes(v));
    const unselected = rawOptionKeys.filter((v) => !selected.includes(v));
    const optionsSelectedButNotInRaw = selectedValues.filter((v) => !rawOptionKeys.includes(v));
    let finalValues;
    if (isMulti) {
      finalValues = [...optionsSelectedButNotInRaw, ...selected, ...unselected];
    } else {
      finalValues = rawOptionKeys;
    }

    // If there is an all value, always put it at the top
    if (finalValues.includes(allValue)) {
      finalValues = [allValue, ...finalValues.filter((v) => v !== allValue)];
    }
    return finalValues;
  }, [rawOptions, selectedValues, isMulti, allValue]);

  const optionsWithTempValues = useMemo(() => {
    const tempValuesNotInOptions = tempSelectedValues.filter((v) => !options.includes(v));
    return [...tempValuesNotInOptions, ...options];
  }, [options, tempSelectedValues]);

  // Generate unique IDs for accessibility
  const listboxId = `${id}-listbox`;
  // Append a random string to the ID to prevent the chrome autocomplete dropdown from showing
  // up.
  const searchFieldId = `${id}-search-${randomString(8)}`;

  // Filter options based on input
  const { options: postSearchOptions, highlightedRanges: postSearchOptionsHighlightedRanges } = useMemo(() => {
    if (!inputValue) {
      return { options: optionsWithTempValues };
    }
    if (filterOptions) {
      const { matches, matchesHighlightedRanges } = filterOptions(optionsWithTempValues, inputValue);
      return {
        options: matches,
        highlightedRanges: matchesHighlightedRanges,
      };
    }
    return {
      options: optionsWithTempValues.filter((option) => option.toLowerCase().includes(inputValue.toLowerCase())),
      highlightedRanges: [],
    };
  }, [optionsWithTempValues, inputValue, filterOptions]);

  const { options: optionsWithCustomValue, customValue } = useMemo(() => {
    // add the input value to the options if it doesn't exist
    if (inputValue && !postSearchOptions.includes(inputValue) && allowCustomValue) {
      return { options: [...postSearchOptions, inputValue], customValue: inputValue };
    }
    return { options: postSearchOptions, customValue: null };
  }, [inputValue, postSearchOptions, allowCustomValue]);

  const filteredOptions = optionsWithCustomValue;

  // Reset highlighted index when input changes
  useEffect(() => {
    const hasSearchValue = inputValue.trim() !== '';
    // If there is a search value, set the highlighted index to the first option, otherwise reset to the default
    setHighlightedIndex(hasSearchValue ? 0 : DEFAULT_HIGHLIGHTED_INDEX);
  }, [inputValue, filteredOptions.length]);

  // Focus management
  useEffect(() => {
    // Ensure the input is focused when opened
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (skipTempValues?: boolean) => {
    setAnchorEl(null);

    setInputValue('');
    setRawInputValue('');
    // reset selected index
    setHighlightedIndex(DEFAULT_HIGHLIGHTED_INDEX);

    // If the selected values have changed, call the onSelect callback
    if (tempSelectedValues.join(',') !== selectedValues.join(',') && skipTempValues !== true && isMulti) {
      onSelect?.(tempSelectedValues);
    }
    // Return focus to main input
    if (mainInputRef.current) {
      const input = mainInputRef.current.querySelector('input');
      if (input) input.focus();
    }
  };

  const handleOptionSelected = (newValue: string | null) => {
    // If the value is null, reset the selected values
    if (newValue === null) {
      onSelect?.(null);
    } else if (isMulti) {
      // If multi, return an array
      onSelect?.([newValue]);
    } else {
      // If single, return a string
      onSelect?.(newValue);
    }
    handleClose(true);
  };

  // Reset selected values when the prop changes
  useEffect(() => {
    setTempSelectedValues(selectedValues);
  }, [selectedValues]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // If the popover is closed, open it
    if (!open) {
      if (isCommandEnterShortcut(event)) {
        event.preventDefault();
      } else if (shouldOpenCombobox(event)) {
        event.preventDefault();
        if (mainInputRef.current) {
          setAnchorEl(mainInputRef.current);
        }
      }
      return;
    }

    const nextItem = () => {
      setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
      if (highlightedIndex >= 0 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: highlightedIndex,
          align: 'center',
        });
      }
    };

    const prevItem = () => {
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      if (highlightedIndex >= 0 && virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: highlightedIndex,
          align: 'center',
        });
      }
    };

    switch (event.key) {
      // Move the highlighted index down
      case 'ArrowDown':
        event.preventDefault();
        nextItem();
        break;

      // Move the highlighted index up
      case 'ArrowUp':
        event.preventDefault();
        prevItem();
        break;

      // If Enter is pressed, select the highlighted option or create a new value
      case 'Enter':
        if (isCommandEnterShortcut(event)) {
          event.preventDefault();
          break;
        }
        event.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          onOptionClick(filteredOptions[highlightedIndex]!);
        } else {
          if (allowCustomValue) {
            handleOptionSelected(inputValue);
          }
        }
        break;

      // Close the popover
      case 'Escape':
        event.preventDefault();
        handleClose();
        break;

      // If Tab is pressed, move the focus to the next item
      case 'Tab':
        event.preventDefault();
        // If shift is pressed, move the focus to the previous item
        if (!event.shiftKey) {
          nextItem();
        } else {
          prevItem();
        }
        break;
      default:
        break;
    }
  };

  const onOptionClick = (option: string) => {
    // If multi, toggle the value and keep the popover open
    if (isMulti) {
      handleTempSelectedValues(option);
    } else {
      handleOptionSelected(option);
    }
  };

  const onOnlyOptionClick = (option: string) => {
    handleTempSelectedValues(option, true);
  };

  const onOptionCheckboxClick = (option: string) => {
    handleTempSelectedValues(option);
  };

  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // Handle main input keyboard events
  const handleMainInputKeyDown = (event: React.KeyboardEvent) => {
    if (isCommandEnterShortcut(event)) {
      event.preventDefault();
    } else if (shouldOpenCombobox(event)) {
      event.preventDefault();
      setAnchorEl(event.currentTarget as HTMLDivElement);
    }
  };

  const handleTempSelectedValues = (value: string, only: boolean = false) => {
    if (only) {
      // If only is true, set only the value
      setTempSelectedValues([value]);
    } else if (value === allValue) {
      // Only set the All value
      setTempSelectedValues([value]);
    } else {
      setTempSelectedValues((prev) => {
        // Toggle the value
        let nextValues = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
        // remove All value
        nextValues = nextValues.filter((v) => v !== allValue);
        return nextValues;
      });
    }

    // Focus the search input back
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const highlightedOption = useMemo(() => {
    if (highlightedIndex < 0 || highlightedIndex > filteredOptions.length) {
      return null;
    }
    const option = filteredOptions[highlightedIndex];
    if (option && option !== customValue) {
      return option;
    } else {
      return null;
    }
  }, [highlightedIndex, filteredOptions, customValue]);

  return {
    selectedValues,
    anchorEl,
    setAnchorEl,
    tempSelectedValues,
    setTempSelectedValues,
    inputValue,
    inputRef,
    getOptionLabel,
    getOptionDescription,
    setInputValue,
    highlightedIndex,
    highlightedOption,
    setHighlightedIndex,
    open,
    options,
    customValue,
    listboxId,
    searchFieldId,
    filteredOptions,
    highlightedRanges: postSearchOptionsHighlightedRanges,
    virtuosoRef,
    handleClick,
    handleClose,
    handleOptionSelected,
    handleKeyDown,
    handleMainInputKeyDown,
    onOptionClick,
    onOnlyOptionClick,
    onOptionCheckboxClick,
    handleTempSelectedValues,
    rawInputValue,
    setRawInputValue,
  };
}

/**
 * The `ComboboxSelect` component should be used for complex multiselect use cases with 10 or more items.
 *
 * For simpler use cases with less than 10 items, you may instead use the `MultiSelect` component.
 */
export function ComboboxSelect(props: ComboboxProps) {
  const height = 400;
  const {
    showFooter = true,
    label,
    renderValue,
    isMulti = false,
    id = 'combobox',
    isLoading,
    disabled,
    noOptionsText = 'No options found',
    highlightSearchKeywords = false,
    renderFooter,
    searchPlaceholder,
    onReset,
    borderColor,
    'data-testid': dataTestId,
    'data-trackid': dataTrackId,
  } = props;
  const t = useTheme();

  const comboboxProps = useCombobox(props);

  const {
    selectedValues,
    anchorEl,
    tempSelectedValues,
    inputValue,
    inputRef,
    highlightedIndex,
    highlightedOption,
    setHighlightedIndex,
    open,
    options,
    customValue,
    listboxId,
    searchFieldId,
    filteredOptions,
    highlightedRanges,
    virtuosoRef,
    handleClick,
    handleClose,
    handleOptionSelected,
    handleKeyDown,
    handleMainInputKeyDown,
    onOptionClick,
    onOnlyOptionClick,
    onOptionCheckboxClick,
    getOptionLabel,
    getOptionDescription,
    rawInputValue,
    setRawInputValue,
  } = comboboxProps;

  const highlightWords = useMemo(() => {
    if (!highlightSearchKeywords) {
      return [];
    }
    return inputValue.split(' ').filter((word) => word.length > 0);
  }, [inputValue, highlightSearchKeywords]);

  return (
    <>
      <Box
        role="combobox"
        display="flex"
        height="100%"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={disabled ? () => {} : handleClick}
        onKeyDown={handleMainInputKeyDown}
        data-testid={dataTestId}
        data-trackid={dataTrackId}
        sx={
          borderColor
            ? {
                [`& .${outlinedInputClasses.root} fieldset`]: {
                  borderColor: borderColor,
                  borderWidth: 2,
                },
              }
            : undefined
        }
      >
        {renderValue ? (
          renderValue
        ) : (
          <ComboboxSelectTrigger
            value={selectedValues.map((v) => {
              return getOptionLabel(v);
            })}
            disabled={disabled}
            label={label}
            maxWidth={INPUT_MAX_WIDTH}
            minWidth={INPUT_MIN_WIDTH}
            placeholder={label}
          />
        )}
      </Box>
      {open && (
        <ClickAwayListener onClickAway={() => handleClose()}>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={() => handleClose()}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                height: height,
                // Add this to prevent layout shifts
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
            disableAutoFocus
            disableEnforceFocus
          >
            <Stack direction={'row'} height={'100%'}>
              <Stack width={400} height={'100%'} sx={{ overflow: 'hidden' }}>
                {/* Fixed header section */}
                <Box
                  sx={{
                    p: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <TextField
                    id={searchFieldId}
                    fullWidth
                    variant="outlined"
                    value={rawInputValue}
                    onChange={(e) => setRawInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={searchPlaceholder ?? 'Search'}
                    inputRef={inputRef}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search
                            fontSize="small"
                            sx={(theme) => ({
                              color: theme.palette.text.disabled,
                            })}
                          />
                        </InputAdornment>
                      ),
                    }}
                    aria-controls={listboxId}
                    aria-activedescendant={highlightedIndex >= 0 ? `${id}-option-${highlightedIndex}` : undefined}
                    aria-autocomplete="list"
                    aria-label={'Search options'}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  />

                  <Typography variant="body2" sx={{ mt: 1, width: '100%', display: 'block' }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent={'space-between'}
                      alignItems={'center'}
                      color={(t) => t.palette.text.legacySecondary}
                    >
                      {isMulti && <div>{tempSelectedValues.length} selected</div>}

                      {onReset !== undefined && (
                        <div>
                          <Button
                            size="small"
                            variant="tertiary"
                            onClick={() => {
                              const value = onReset();
                              handleOptionSelected(value);
                            }}
                          >
                            <Typography sx={{ fontSize: (t) => t.typography.body2, fontWeight: 'bold' }}>
                              Reset
                            </Typography>
                          </Button>
                        </div>
                      )}
                    </Stack>
                  </Typography>
                </Box>
                {isLoading && <LinearProgress />}
                <Box
                  id={listboxId}
                  sx={{
                    flexGrow: 1,
                    width: '100%',
                  }}
                  role="listbox"
                  aria-multiselectable={isMulti ? 'true' : undefined} // Added for multi-select
                  aria-label="Options list" // Alternatively, tie this to a visible label
                >
                  {isLoading && options.length === 0 && (
                    <Box m={1}>
                      <Skeleton />
                      <Skeleton />
                      <Skeleton />
                      <Skeleton />
                    </Box>
                  )}
                  {!isLoading && (
                    <>
                      {filteredOptions.length > 0 ? (
                        <Virtuoso
                          ref={virtuosoRef}
                          style={{ minHeight: '100%', width: '100%' }}
                          totalCount={filteredOptions.length}
                          overscan={20}
                          increaseViewportBy={200}
                          itemContent={(index) => {
                            const option = filteredOptions[index];
                            if (option === undefined) {
                              return null;
                            }

                            const optionHighlightedRanges = highlightedRanges?.[index] ?? undefined;

                            const isCustomValue = option === customValue;
                            const isSelected = tempSelectedValues.includes(option);
                            const isHighlighted = highlightedIndex === index;
                            const isAllOption = option === DEFAULT_ALL_VALUE;

                            let backgroundColor = isSelected ? t.theme.palette.background.selected : 'transparent';
                            if (isHighlighted) {
                              backgroundColor = t.theme.palette.background.hover;
                            }

                            const optionDescription = getOptionDescription(option);
                            const optionLabel = getOptionLabel(option);

                            const optionText = isCustomValue ? `"${optionLabel}"` : optionLabel;

                            let highlightedOptions: React.ReactNode | string = optionText;
                            if (!isCustomValue && highlightWords.length > 0) {
                              if (optionHighlightedRanges) {
                                highlightedOptions = highlightRangesInString(optionText, optionHighlightedRanges);
                              } else {
                                highlightedOptions = highlightWordsInString(optionText, highlightWords);
                              }
                            }

                            return (
                              <>
                                <ListItemOnly
                                  onMouseOver={() => {
                                    setHighlightedIndex(index);
                                  }}
                                  disable={!isMulti || isCustomValue || isAllOption}
                                  onOnlyClick={(e) => {
                                    e.stopPropagation();
                                    onOnlyOptionClick(option);
                                    setHighlightedIndex(index);
                                  }}
                                  id={`${id}-option-${index}`}
                                  onClick={() => {
                                    // If the user highlighting text, don't click
                                    if (window.getSelection()?.toString()) {
                                      return;
                                    }
                                    onOptionClick(option);
                                    setHighlightedIndex(index);
                                  }}
                                  sx={{
                                    width: '100%',
                                    cursor: 'pointer',
                                    paddingY: optionDescription ? 0 : undefined,
                                    backgroundColor: backgroundColor,
                                    '&:hover': {
                                      backgroundColor: t.theme.palette.background.hover,
                                    },
                                    '&:focus': {
                                      backgroundColor: t.theme.palette.background.hover,
                                      // no outline
                                      outline: 'none',
                                      boxShadow: 'none',
                                    },
                                  }}
                                  dense
                                  role="option"
                                  aria-selected={isHighlighted}
                                  tabIndex={0}
                                >
                                  {isMulti && (
                                    <Checkbox
                                      sx={{ p: 0, mr: 1 }}
                                      checked={isSelected}
                                      tabIndex={-1}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onOptionCheckboxClick(option);
                                        setHighlightedIndex(index);
                                      }}
                                    />
                                  )}

                                  {isCustomValue && !isMulti && (
                                    <PlusIcon
                                      scale={0.8}
                                      sx={{ pr: 0.5, color: (t) => t.palette.text.legacySecondary }}
                                    />
                                  )}

                                  <ListItemText
                                    primaryTypographyProps={{ noWrap: true, title: optionLabel }}
                                    secondaryTypographyProps={{ noWrap: true, title: optionDescription }}
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                    primary={highlightedOptions}
                                    secondary={isCustomValue && isMulti ? 'Create value' : optionDescription}
                                  />

                                  {!isMulti && isSelected && (
                                    <CheckIcon sx={{ color: (t) => t.palette.primary.main }} />
                                  )}
                                </ListItemOnly>
                              </>
                            );
                          }}
                        />
                      ) : (
                        <ListItem role="option">
                          <ListItemText primary={noOptionsText} sx={{ color: t.theme.palette.text.legacySecondary }} />
                        </ListItem>
                      )}
                    </>
                  )}
                </Box>
                {showFooter && (
                  <Box
                    sx={{
                      p: 1,
                      borderTop: 1,
                      borderColor: 'divider',
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 1,
                    }}
                  >
                    {renderFooter ? (
                      renderFooter(comboboxProps)
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ width: '100%', display: 'block', color: (t) => t.palette.text.legacySecondary }}
                      >
                        <Stack direction="row" spacing={1} justifyContent={'space-between'}>
                          <div>
                            {numbro(filteredOptions.length).format({
                              thousandSeparated: true,
                            })}{' '}
                            values
                          </div>
                        </Stack>
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
              {props.secondaryContent &&
                props.secondaryContent({ highlightedOptionValue: highlightedOption ?? undefined, handleClose })}
            </Stack>
          </Popover>
        </ClickAwayListener>
      )}
    </>
  );
}

type ListItemOnlyProps = ListItemProps & {
  disable?: boolean;
  force?: boolean;
  onOnlyClick?: (e: React.MouseEvent) => void;
};

function ListItemOnly({ disable, force, onOnlyClick, children, ...rest }: ListItemOnlyProps) {
  const element = (hovered: boolean) => (
    <ListItem {...rest}>
      {children}
      {!disable && (hovered || force) && (
        <Box
          component="span"
          sx={{ color: (t) => t.palette.text.legacySecondary, fontSize: (t) => t.typography.body2 }}
          onClick={onOnlyClick}
        >
          Only
        </Box>
      )}
    </ListItem>
  );

  const [hoverable] = useHover(element);
  return <>{hoverable}</>;
}

export function ComboboxSelectTrigger(props: {
  /**
   * The current selected value or values
   */
  value: string | string[] | null;
  /**
   * Whether the trigger is disabled
   */
  disabled?: boolean;
  /**
   * The label for the input
   */
  label?: string;
  /**
   * The maximum width of the input
   */
  maxWidth?: string | number;
  /**
   * The minimum width of the input
   */
  minWidth?: string | number;
  /**
   * The placeholder text for the input
   */
  placeholder?: string;
}) {
  const { minWidth, maxWidth, value } = props;

  let valueStr = '';
  if (Array.isArray(value) && value.length > 0) {
    valueStr = value.join(', ');
  } else if (typeof value === 'string') {
    valueStr = value;
  }

  // Hacky workaround to get the placeholder to show up (as a label) when the value is not specified
  let labelText: string | undefined;
  if (props.label) {
    labelText = props.label;
  } else if (valueStr === '') {
    labelText = props.placeholder;
  }

  const textWidth = useTextWidth(valueStr);
  // TODO: handle custom CSS string widths as well. This currently only handles numeric widths.
  // We should move to a model where we measure the size of the trigger button rather than using
  // these hard-coded values for comparisons.
  const horizontalPadding = 16;
  const isOverflowing = typeof maxWidth === 'number' && maxWidth < textWidth + horizontalPadding;

  return (
    <Tooltip Content={isOverflowing ? valueStr : ''}>
      <TextField
        InputLabelProps={{
          disabled: props.label === undefined,
        }}
        label={labelText}
        placeholder={props.placeholder}
        disabled={props.disabled}
        variant="outlined"
        fullWidth
        value={valueStr}
        sx={{
          minWidth: minWidth,
          maxWidth: maxWidth,
          cursor: 'pointer',
        }}
        select
        InputProps={{
          sx: { cursor: 'pointer' },
          readOnly: true,
        }}
      >
        <option key={valueStr} value={valueStr}>
          {valueStr}
        </option>
      </TextField>
    </Tooltip>
  );
}

/**
 * Highlights the ranges in the given text.
 * @param text - The text to highlight.
 * @param ranges - The ranges to highlight. The ranges are a flat array of numbers that indicate the start and end of the highlighted range.
 * This is a flat array to reduce the number of allocations, expect an even amount of numbers.
 * Example value us [0, 4, 8, 10], which indicates that char[0:4] and char[8:10] are highlighted.
 * @returns The highlighted text.
 */
export function highlightRangesInString(text: string, ranges: number[]): React.ReactNode {
  if (!text || ranges.length === 0 || ranges.length % 2 !== 0) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // Process ranges in pairs (start, end)
  for (let i = 0; i < ranges.length; i += 2) {
    const start = ranges[i];
    const end = ranges[i + 1];

    // Skip if we don't have a complete pair
    if (start === undefined || end === undefined) {
      continue;
    }

    // Add text before the highlighted range
    if (lastIndex < start) {
      parts.push(text.slice(lastIndex, start));
    }

    // Add the highlighted text
    if (start < end && start < text.length) {
      const highlightedText = text.slice(start, Math.min(end, text.length));
      parts.push(
        <span key={`highlight-${start}-${end}`} style={{ fontWeight: 'bold' }}>
          {highlightedText}
        </span>
      );
    }

    lastIndex = Math.max(lastIndex, end);
  }

  // Add any remaining text after the last range
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}

/**
 * Highlights the words in the given text that match the words in the array.
 */
function highlightWordsInString(text: string, words: string[]): React.ReactNode {
  if (!text) {
    return '';
  }

  // Escape regex special characters in each word to ensure exact string matching
  const escapedWords = words.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  let regex;
  try {
    regex = new RegExp(`(${escapedWords.join('|')})`, 'gi');
  } catch {
    return text;
  }
  const parts = text.split(regex);
  return parts.map((part, index) => {
    if (words.some((word) => word.toLowerCase() === part.toLowerCase())) {
      return (
        <span key={index} style={{ fontWeight: 'bold' }}>
          {part}
        </span>
      );
    }
    return part;
  });
}

// Cmd-Enter and Ctrl-Enter are special shortcuts that run a query in different parts of the app.
function isCommandEnterShortcut(event: React.KeyboardEvent) {
  return event.key === 'Enter' && isCommandKeyPressed(event.nativeEvent);
}
